<template>
  <div v-if="loading">
    <p-o-s-page-skeleton variant="table" />
  </div>
  <div v-else-if="!hasPermission">
    <p-o-s-access-denied />
  </div>
  <div v-else>
    <div class="mb-3">
      <!-- Row 1: zone tabs (scrollable) + the Select action pinned right -->
      <div class="d-flex align-items-center gap-2">
        <div class="zone-wrapper flex-grow-1" style="min-width: 0;">
          <ul class="nav seg-filter flex-nowrap">
            <li
              class="cursor-pointer nav-item"
              @click="() => {
                if (params.zone_id == '') return;
                params.zone_id = '';
                fetchData();
              }"
            >
              <a
                class="nav-link"
                :class="{ 'active': params.zone_id == '' }"
              >
                {{ $t('All') }}
              </a>
            </li>
            <li
              class="cursor-pointer nav-item"
              v-for="zone in filter.zones"
              @click="() => {
                if (params.zone_id == zone.id) return;
                params.zone_id = zone.id;
                fetchData();
              }"
            >
              <a
                class="nav-link"
                :class="{ 'active': zone.id == params.zone_id }"
              >
                {{ $t(zone.name) }}
              </a>
            </li>
          </ul>
        </div>
        <button
          v-if="!selectMode"
          type="button"
          class="btn btn-sm btn-outline-theme rounded-pill px-3 flex-shrink-0"
          :disabled="!selectableItems.length"
          @click="enterSelectMode"
        >
          <i class="fa-solid fa-square-check me-1" />
          {{ $t('Select') }}
        </button>
      </div>

      <!-- Row 2 (normal): status count badges on their own line -->
      <div v-if="!selectMode" class="d-flex align-items-center gap-2 flex-wrap mt-2">
        <span
          class="badge rounded-pill px-3 py-2"
          :class="$storeIndex.backgroundCSSTableSatus(key)"
          v-for="(count, key) in stateCounts"
        >
          {{ count + ' ' + stateLabel(key) }}
        </span>
      </div>

      <!-- Row 2 (select mode): bulk-actions toolbar, full width -->
      <bulk-print-qr-toolbar
        v-else
        class="mt-2"
        :count="selectedIds.length"
        :total="selectableItems.length"
        :all-selected="allSelectedQr"
        :qr-count="qrSelectedCount"
        :open-count="openSelectedCount"
        :close-count="qrSelectedCount"
        :show-open="true"
        :show-print-qr="$hasFeature('feature_qr_ordering')"
        :busy="busy"
        :busy-label="busyLabel"
        @toggle-all="toggleAllQr"
        @print-qr="printSelectedQr"
        @open="openSelected"
        @close="closeSelected"
        @cancel="exitSelectMode"
      />
    </div>
    <div
      class="table-container"
      v-if="data.length > 0"
    >
      <!-- R2 card — ใช้ทุกจอ (grid หลายคอลัมน์บน desktop เหมือนหน้า session) -->
      <div
        v-for="table in data"
        :key="table.id"
        class="table-card position-relative"
        :class="[
          $storeIndex.CSSStatusTableItem(table.state),
          {
            'qr-select-on': selectMode && isSelectable(table),
            'qr-selected': selectMode && selectedIds.includes(table.id),
            'qr-select-dim': selectMode && !isSelectable(table),
          }
        ]"
        @click="onCardClick(table)"
      >
        <span class="tc-rail"/>
        <!-- HEAD: ชื่อโต๊ะ (+ chip แจ้งเตือน) · ค่าสถานะ/ยอดเงิน -->
        <div class="tc-head">
          <div class="tc-titles">
            <span class="tc-name">{{ table.name }}</span>
            <span
              v-if="table.state === 'Occupied' && table.is_request"
              class="alert-chip alert-chip--call"
              :title="$t('Customer is calling staff')"
            >
              <i class="fa-solid fa-bell"/>
            </span>
            <span
              v-if="table.state === 'Occupied' && table.is_bill_requested"
              class="alert-chip alert-chip--bill"
              :title="$t('Customer requested the bill')"
            >
              <i class="fa-solid fa-file-invoice-dollar"/>
            </span>
          </div>
          <div class="tc-value">
            <span v-if="table.state === 'Occupied'" class="tc-amount">
              {{ $storeIndex.formatThaiCurrency(table.sale_total) }}
            </span>
            <!-- เปิดโต๊ะแล้วยังไม่สั่ง — แพ็กไม่มี QR ใช้ป้าย "รอสั่ง" (ไม่มี QR ให้ "พร้อม") ไอคอนนิ่ง ไม่กะพริบ -->
            <span v-else-if="table.state === 'Qr Ready'" class="tc-qr">
              <template v-if="qrOrdering"><span class="tc-qr__dot"/>{{ $t('QR Ready') }}</template>
              <template v-else><i class="fa-solid fa-bell-concierge"/>{{ $t('Waiting for order') }}</template>
            </span>
            <span v-else-if="table.state === 'Available'" class="tc-badge tc-badge--available">
              <i class="fa-solid fa-circle-check me-1"/>{{ $t('_Table.Available') }}
            </span>
            <span v-else-if="table.state === 'Reserved'" class="tc-badge tc-badge--reserved">
              <i class="fa-regular fa-calendar-check me-1"/>{{ table.reserve_reserve_at ? formatTime(table.reserve_reserve_at) : '-' }}
            </span>
            <span v-else class="tc-badge tc-badge--maint">
              <i class="fa-solid fa-screwdriver-wrench me-1"/>{{ $t('Maintenance') }}
            </span>
          </div>
        </div>

        <div class="tc-divider"/>

        <!-- FOOT: ทุกสถานะมี footer 1 บรรทัด → การ์ดสูงเท่ากันหมด -->
        <div class="tc-foot">
          <template v-if="table.state === 'Occupied'">
            <span class="tc-staff"><i class="fa-solid fa-user-tie me-1"/>{{ table.service_staff }}</span>
            <span class="tc-foot-item"><i class="fa-solid fa-users"/>{{ table.service_pax }}</span>
            <span class="tc-foot-item tc-foot-end">
              <i class="fa-regular fa-clock"/>{{ (table.service_activated_at || table.service_created_at) ? $storeIndex.getTimeDiff(table.service_activated_at || table.service_created_at) : '-' }}
            </span>
          </template>
          <template v-else-if="table.state === 'Qr Ready'">
            <span class="tc-staff"><i class="fa-solid fa-user-tie me-1"/>{{ table.service_staff }}</span>
            <span class="tc-foot-item tc-foot-end">
              <i class="fa-regular fa-clock"/>{{ table.service_created_at ? $storeIndex.getTimeDiff(table.service_created_at) : '-' }}
            </span>
          </template>
          <template v-else-if="table.state === 'Reserved'">
            <span class="tc-foot-item"><i class="fa-solid fa-users"/>{{ table.service_pax }} {{ $t('_Table.Guests') }}</span>
            <span v-if="table.reserve_name" class="tc-foot-item tc-foot-end"><i class="fa-solid fa-user"/>{{ table.reserve_name }}</span>
          </template>
          <template v-else>
            <span class="tc-foot-item"><i class="fa-solid fa-user-group"/>{{ table.seat_capacity }} {{ $t('_Table.Seats') }}</span>
          </template>
        </div>
      </div>
    </div>
    <div
      v-else
    >
      {{ $t('No Available Data') }}
    </div>
  </div>
  <session-actions-modal
    v-if="actionsModal.open"
    variant="table"
    :session="actionsModal.session"
    :table-id="actionsModal.tableId"
    @close="() => {
      actionsModal.open = false;
      actionsModal.tableId = 0;
    }"
    @check-bill="openCheckBill"
    @move-table="openMoveTable"
    @merge-session="openMergeSession"
    @manage-requests="openManageRequests"
    @closed="onTableClosed"
  />
  <modal-service-requests
    v-if="modalServiceRequests.open"
    :token="modalServiceRequests.token"
    :name="modalServiceRequests.name"
    @close="() => {
      modalServiceRequests.open = false;
      modalServiceRequests.token = '';
    }"
    @changed="fetchData"
  />
  <modal-table-reserved
    v-if="modalTableReserved.open"
    :id="modalTableReserved.id"
    :reservation-id="modalTableReserved.reservationId"
    :name="modalTableReserved.name"
    :service-token="modalTableReserved.serviceToken"
    :reserve-name="modalTableReserved.reserveName"
    :reserve-tel="modalTableReserved.reserveTel"
    :reserve-at="modalTableReserved.reserveAt"
    :reserve-pax="modalTableReserved.reservePax"
    :reserve-note="modalTableReserved.reserveNote"
    @close="() => {
      modalTableReserved.open = false;
      modalTableReserved.id = 0;
      modalTableReserved.reservationId = 0;
    }"
    @move-table="openMoveTableReserved"
  />
  <offcanvas-check-bill
    v-if="checkBill.open"
    :session-name="checkBill.name"
    :session-token="checkBill.serviceToken"
    @close="checkBill.open = false"
  />
  <offcanvas-move-table
    v-if="moveTable.open"
    :token="moveTable.token"
    :table-id="moveTable.id"
    :table-name="moveTable.name"
    :table-total="moveTable.total"
    @close="moveTable.open = false"
    @moved="onTableMoved"
  />
  <offcanvas-merge-session
    v-if="mergeSession.open"
    :host-token="mergeSession.hostToken"
    @close="mergeSession.open = false"
    @merged="onSessionsMerged"
  />
  <modal-table-management
    v-if="modalTableManagement.open"
    :id="modalTableManagement.id"
    :name="modalTableManagement.name"
    @close="() => {
      modalTableManagement.open = false;
      modalTableManagement.id = 0;
    }"
  />
</template>
<script lang="js">
import POSPageSkeleton from '@/plugins/POSPageSkeleton.vue';
import POSAccessDenied from "@/plugins/POSAccessDenied.vue";
import ModalTableManagement from './ModalTableManagement.vue';
import BulkPrintQrToolbar from '@/components/pos/BulkPrintQrToolbar.vue';
import SessionActionsModal from '@/components/pos/sessions/SessionActionsModal.vue';
import ModalTableReserved from './ModalTableReserved.vue';
import ModalServiceRequests from './ModalServiceRequests.vue';
import OffcanvasMoveTable from './OffcanvasMoveTable.vue';
import OffcanvasMergeSession from './OffcanvasMergeSession.vue';
import OffcanvasCheckBill from '@/components/shared/billing/OffcanvasCheckBill.vue';
import { POSTables, Sessions } from '@/services/endpoint/pos.service';
import moment from "moment";
import numeral from 'numeral';
import { usePOSBranchSocket } from '@/composables/usePOSBranchSocket';
import { useShopStore } from '@/stores/shop.store';
import { printQrSlips, serviceUrl } from '@/utils/qrPrint';
import { playNotifySound, NOTIFY_SOUNDS } from '@/composables/useNotifySound';

export default {
  data() {
    return {
      params: {
        zone_id: ''
      },
      modalTableManagement: {
        open: false,
        id: 0
      },
      // In-page "select & bulk actions" mode (toggled from the header button).
      selectMode: false,
      selectedIds: [],
      printing: false,
      printedCount: 0,
      opening: false,
      closing: false,
      // Unified table/session action sheet. `session` is the normalized
      // object <session-actions-modal> expects; `tableId` drives the
      // table-summary fetch.
      actionsModal: {
        open: false,
        tableId: 0,
        session: null,
      },
      // Staff-side service-request inbox for the selected table. Opens
      // from inside <session-actions-modal> via @manage-requests, so the
      // action sheet closes itself (data-bs-dismiss) before this opens.
      modalServiceRequests: {
        open: false,
        token: '',
        name: ''
      },
      modalTableReserved: {
        open: false,
        id: 0,
        reservationId: 0,
        name: '',
        serviceToken: '',
        reserveName: '',
        reserveTel: '',
        reserveAt: '',
        reservePax: 0,
        reserveNote: '',
      },
      moveTable: {
        open: false,
        token: '',
        id: 0,
        name: '',
        total: 0
      },
      mergeSession: {
        open: false,
        hostToken: ''
      },
      checkBill: {
        open: false,
        name: '',
        serviceToken: ''
      },
      data: {},
      filter: {},
      loading: false,
      hasPermission: false,
    }
  },
  components: {
    POSPageSkeleton,
    POSAccessDenied,
    SessionActionsModal,
    ModalTableReserved,
    ModalTableManagement,
    ModalServiceRequests,
    OffcanvasCheckBill,
    OffcanvasMoveTable,
    OffcanvasMergeSession,
    BulkPrintQrToolbar
  },
  async mounted() {
    const { branchId } = useShopStore();

    usePOSBranchSocket({
      branchId,
      onRequested: this.websocketRequested,
      onBillRequested: this.websocketBillRequested,
      onOpen: this.websocketSessionOpen,
      onClose: this.websocketSessionClose,
    });

    await this.fetchData();
  },
  methods: {
    /** ป้ายปุ่มนับสถานะด้านบน — key คือชื่อสถานะจาก API ('Qr Ready' / 'Occupied' / ...) */
    stateLabel(key) {
      if (key === 'Qr Ready' && !this.qrOrdering) return this.$t('Waiting for order');
      return this.$t(key);
    },
    websocketRequested(data) {
      playNotifySound(NOTIFY_SOUNDS.requested);
      const table = this.data.find(i => i.service_id === Number(data.id));
      if (table) {
        table.is_request = true;
      } else {
        this.fetchData();
      }
    },
    websocketBillRequested(data) {
      playNotifySound(NOTIFY_SOUNDS.checkBill);
      const table = this.data.find(i => i.service_id === Number(data.id));
      if (table) {
        table.is_bill_requested = true;
      } else {
        this.fetchData();
      }
    },
    websocketSessionOpen(data) {
      const table = this.data.find(i => i.id === Number(data.table_id));
      if (table) {
        table.service_created_at = data.created_at;
        table.service_id = data.id;
        table.service_pax = data.service_pax;
        table.service_staff = data.service_staff;
        table.service_token = data.token;
        table.state = data.table_state;
      } else {
        this.fetchData();
      }
    },
    websocketSessionClose(data) {
      this.fetchData();
    },
    openCheckBill() {
      // Bills are fetched inside <offcanvas-check-bill> via the session token.
      this.checkBill.name = this.actionsModal.session.name;
      this.checkBill.serviceToken = this.actionsModal.session.token;
      this.checkBill.open = true;
    },
    openMoveTable() {
      this.moveTable.token = this.actionsModal.session.token;
      this.moveTable.id = this.actionsModal.tableId;
      this.moveTable.name = this.actionsModal.session.name;
      this.moveTable.total = 0;
      this.moveTable.open = true;
    },
    openManageRequests() {
      // The action sheet closed itself via data-bs-dismiss; carry the
      // session token/name into the requests inbox so it can fetch without
      // an extra round-trip.
      this.modalServiceRequests.token = this.actionsModal.session.token;
      this.modalServiceRequests.name = this.actionsModal.session.name;
      this.modalServiceRequests.open = true;
    },
    openMoveTableReserved() {
      this.moveTable.token = this.modalTableReserved.serviceToken;
      this.moveTable.id = this.modalTableReserved.id;
      this.moveTable.name = this.modalTableReserved.name;
      this.moveTable.total = 0;
      this.moveTable.open = true;
    },
    onTableMoved() {
      this.moveTable.open = false;
      this.fetchData();
    },
    isSelectable(table) {
      return this.selectableIdSet.has(table.id);
    },
    enterSelectMode() {
      if (!this.selectableItems.length) return;
      this.selectMode = true;
      this.selectedIds = [];
    },
    exitSelectMode() {
      this.selectMode = false;
      this.selectedIds = [];
    },
    onCardClick(table) {
      if (this.selectMode) {
        if (!this.isSelectable(table)) return;
        const i = this.selectedIds.indexOf(table.id);
        if (i === -1) this.selectedIds.push(table.id);
        else this.selectedIds.splice(i, 1);
        return;
      }
      this.clickTable(table);
    },
    toggleAllQr() {
      this.selectedIds = this.allSelectedQr ? [] : this.selectableItems.map(it => it.id);
    },
    async printSelectedQr() {
      const chosen = this.selectedItems.filter(it => it.token);
      if (!chosen.length) return;
      this.printing = true;
      this.printedCount = 0;
      try {
        const slips = chosen.map(it => ({
          heading: it.heading,
          subtitle: this.$t('Scan to Service'),
          url: serviceUrl(it.token),
        }));
        const res = await printQrSlips(slips, {
          onProgress: n => { this.printedCount = n; },
        });
        this.$toast.success(this.$t('Print QR Code') + ' ✓ (' + res.count + ')');
        this.exitSelectMode();
      } catch (e) {
        console.error('Bulk QR print error:', e);
        this.$toast.error(this.$t('Print failed') + ': ' + (e.message || e));
      } finally {
        this.printing = false;
      }
    },
    async openSelected() {
      const tableIds = this.selectedItems.filter(it => it.openable).map(it => it.id);
      if (!tableIds.length) return;
      this.opening = true;
      try {
        const res = await Sessions.openBatch({ table_ids: tableIds });
        const n = res?.data?.data?.count ?? tableIds.length;
        this.$toast.success('<b>' + this.$t('Success') + '</b> ' + this.$t('Open Session') + ' (' + n + ')');
        this.exitSelectMode();
        this.fetchData();
      } catch (e) {
        if (e?.response?.data?.message) this.$toast.warn(e.response.data.message);
        else this.$toast.error(this.$t('Open failed') + ': ' + (e.message || e));
      } finally {
        this.opening = false;
      }
    },
    async closeSelected() {
      const tokens = this.selectedItems.filter(it => it.token).map(it => it.token);
      if (!tokens.length) return;

      const confirm = await this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: this.$t('End Service') + ' ' + tokens.length + ' ' + this.$t('items') + '?',
        html: `<div class="text-start mb-2">${this.$t('This will close the service session and release the table for new guests.')}</div>`
          + `<div class="small text-muted text-start"><i class="fa-solid fa-circle-info me-1"></i>${this.$t('Any unpaid bills will remain on file — settle them first if needed.')}</div>`,
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('End Service'),
        buttonsStyling: false,
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary me-2' },
        reverseButtons: true
      });
      if (!confirm.isConfirmed) return;

      this.closing = true;
      try {
        const res = await Sessions.closeBatch({ tokens });
        const n = res?.data?.data?.count ?? tokens.length;
        this.$toast.success('<b>' + this.$t('Success') + '</b> ' + this.$t('End Service') + ' (' + n + ')');
        this.exitSelectMode();
        this.fetchData();
      } catch (e) {
        if (e?.response?.data?.message) this.$toast.warn(e.response.data.message);
        else this.$toast.error(this.$t('Close failed') + ': ' + (e.message || e));
      } finally {
        this.closing = false;
      }
    },
    openMergeSession() {
      // Candidates are fetched inside the offcanvas via the host token.
      this.mergeSession.hostToken = this.actionsModal.session.token;
      this.mergeSession.open = true;
    },
    onSessionsMerged() {
      this.mergeSession.open = false;
      this.fetchData();
    },
    onTableClosed() {
      this.actionsModal.open = false;
      this.actionsModal.tableId = 0;
      this.fetchData();
    },
    clickTable(table) {
      if (table.state == 'Available') {
        this.modalTableManagement.open = true;
        this.modalTableManagement.id = table.id;
        this.modalTableManagement.name = table.name;
      } else if (table.state == 'Occupied' || table.state == 'Qr Ready') {
        // qr_ready = เปิดโต๊ะ/วาง QR แล้วแต่ยังไม่มีออเดอร์ — เปิด modal จัดการเดียวกับ occupied
        // (ดู QR / ย้ายโต๊ะ / ปิด session ได้ แม้ยังไม่มีบิล)
        this.actionsModal.tableId = table.id;
        this.actionsModal.session = {
          token: table.service_token,
          name: table.name,
          table_name: table.name,
          type: 'dine_in',
          service_staff: table.service_staff,
          // เวลาเริ่มบริการจริง = activated_at (ออเดอร์แรก); qr_ready ยังไม่ activate → fallback เวลาเปิดโต๊ะ
          service_at: table.service_activated_at || table.service_created_at,
          is_request: !!table.is_request,
        };
        this.actionsModal.open = true;
      } else if (table.state == 'Reserved') {
        this.modalTableReserved.open = true;
        this.modalTableReserved.id = table.id;
        this.modalTableReserved.reservationId = table.reserve_id || 0;
        this.modalTableReserved.name = table.name;
        this.modalTableReserved.serviceToken = table.service_token;
        this.modalTableReserved.reserveName = table.reserve_name || '';
        this.modalTableReserved.reserveTel = table.reserve_telephone || '';
        this.modalTableReserved.reserveAt = table.reserve_reserve_at || '';
        this.modalTableReserved.reservePax = table.seat_capacity || 0;
        this.modalTableReserved.reserveNote = table.reserve_note || '';
      }
    },
    setParamsBeforeFetchData() {
      let params = _.cloneDeep(this.params);
      return $.each(params, (key, value) => {
        if (value === "" || value.length === 0) {
          delete params[key];
        }
      });
    },
    async fetchData() {
      this.loading = true;
      var queryString = '?' + $.param(this.setParamsBeforeFetchData());
      await POSTables.list(queryString).then(res => {
        this.data = res.data.data;
        this.filter = res.data.filter;
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        }
      });
      this.loading = false;
      return;
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    formatTime(value) {
      return moment(value).format('HH:mm');
    },
    formatNumberDigit(value) {
      return numeral(value).format("0,0.00");
    },
    formatNumber(value) {
      return numeral(value).format("0,0");
    },
  },
  watch: {
  },
  computed: {
    // แพ็กมีสั่งผ่าน QR ไหม — ไม่มี = สถานะ qr_ready (เปิดแล้วยังไม่สั่ง) ขึ้นป้าย "รอสั่ง" แทน "QR พร้อม"
    qrOrdering() {
      return this.$hasFeature('feature_qr_ordering');
    },
    // โต๊ะที่เลือกได้ในโหมด bulk: ว่าง (เปิดได้) หรือ active (พิมพ์ QR / ปิดได้).
    // Reserved / Maintenance เลือกไม่ได้ (dim). openable = โต๊ะว่าง, token = active
    selectableItems() {
      return (Array.isArray(this.data) ? this.data : [])
        .filter(t => ['Available', 'Occupied', 'Qr Ready'].includes(t.state))
        .map(t => ({
          id: t.id,
          name: t.name,
          heading: this.$t('Table') + ' ' + t.name,
          token: t.service_token || null,
          openable: t.state === 'Available',
        }));
    },
    selectableIdSet() {
      return new Set(this.selectableItems.map(it => it.id));
    },
    selectedItems() {
      return this.selectableItems.filter(it => this.selectedIds.includes(it.id));
    },
    qrSelectedCount() {
      return this.selectedItems.filter(it => it.token).length;
    },
    openSelectedCount() {
      return this.selectedItems.filter(it => it.openable).length;
    },
    allSelectedQr() {
      return this.selectableItems.length > 0 && this.selectedIds.length === this.selectableItems.length;
    },
    busy() {
      return this.printing || this.opening || this.closing;
    },
    busyLabel() {
      if (this.printing) return this.$t('Printing') + ' ' + this.printedCount + '/' + this.qrSelectedCount;
      if (this.opening || this.closing) return this.$t('Processing');
      return '';
    },
    // สร้างฟังก์ชันนับ state ใน computed
    stateCounts() {
      return this.data.reduce((acc, table) => {
        const state = table.state;
        // ถ้ายังไม่มี state นี้ใน object ให้เซ็ตค่าเริ่มต้นเป็น 0
        if (!acc[state]) {
          acc[state] = 0;
        }
        // บวก 1 เข้าไป
        acc[state]++;
        return acc;
      }, {}); // {} คือค่าเริ่มต้นเป็น Object ว่างๆ
    }
  }
}
</script>

<style scoped>
/* ---- In-page bulk-QR selection mode -------------------------------------- */
/* Printable tables (Occupied / Qr Ready) get a pointer + a check badge; the
   chosen ones a dark ring; other tables dim and ignore taps. Extra bottom
   padding keeps the last row clear of the fixed action bar. */
/* Selection = orange ring + full-card orange wash (no floating check badge —
   the card content is too packed for one to sit without overlapping). */
.qr-select-on {
  cursor: pointer;
  transition: box-shadow .12s ease, transform .12s ease;
}
.qr-selected {
  box-shadow: 0 0 0 2.5px #E96C15, 0 6px 16px rgba(233, 108, 21, .25);
  border-radius: 12px;
}
.qr-selected::before {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(233, 108, 21, .16);
  z-index: 2;
  pointer-events: none;
}
.qr-select-dim {
  opacity: .4;
  filter: grayscale(0.3);
  pointer-events: none;
}

/* ---- R2 table card (ทุกจอ — grid หลายคอลัมน์บน desktop เหมือนหน้า session) -- */
.table-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-self: start; /* อย่ายืดการ์ดเตี้ย (ว่าง) ให้สูงเท่าการ์ดในแถวเดียวกัน */
  background: #fff;
  border: 1px solid #ece8e2;
  border-radius: 15px;
  box-shadow: 0 1px 2px rgba(30, 25, 20, .05), 0 3px 10px rgba(30, 25, 20, .05);
  padding: 13px 15px 13px 18px;
  cursor: pointer;
  transition: box-shadow .18s ease, transform .12s ease;
  overflow: hidden;
}
.table-card:active { transform: scale(.99); }
@media (min-width: 768px) {
  .table-card:hover {
    box-shadow: 0 1px 2px rgba(30, 25, 20, .05), 0 8px 20px rgba(30, 25, 20, .10);
    transform: translateY(-2px);
  }
}

.tc-rail { position: absolute; left: 0; top: 0; bottom: 0; width: 5px; background: #ced4da; }
.table-card.status-occupied  { background: #fdf4f3; }
.table-card.status-occupied  .tc-rail { background: #d64550; }
.table-card.status-waiting   { background: #f4f8ff; }
.table-card.status-waiting   .tc-rail { background: #2f74e0; }
.table-card.status-available .tc-rail { background: #2b9d5f; }
.table-card.status-reserved  { background: #fffbf3; }
.table-card.status-reserved  .tc-rail { background: #e0a92e; }

/* หัวการ์ด: ชื่อโต๊ะ (ซ้าย) · ค่าสถานะ/ยอดเงิน (ขวา) — บรรทัดเดียว */
.tc-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; min-height: 34px; }
.tc-titles { display: flex; align-items: center; gap: 8px; min-width: 0; }
.tc-name {
  font-size: 1.4rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -.01em;
  color: #22242a;
}
.table-card.status-occupied  .tc-name { color: #c0303c; }
.table-card.status-waiting   .tc-name { color: #1f5fc4; }
.table-card.status-available .tc-name { color: #1f8a52; }
.table-card.status-reserved  .tc-name { color: #b07d13; }

.tc-value { flex: 0 0 auto; display: flex; align-items: center; }
.tc-amount {
  font-size: 1.4rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -.02em;
  color: #c0303c;
  font-variant-numeric: tabular-nums;
}
.tc-qr {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: .78rem; font-weight: 700; color: #1f5fc4;
  background: #eef4ff; border: 1px solid rgba(47, 116, 224, .25);
  padding: 4px 11px; border-radius: 999px; white-space: nowrap;
}
.tc-qr__dot { width: 6px; height: 6px; border-radius: 50%; background: #2f74e0; animation: qrReadyPulse 1.8s ease-out infinite; }
.tc-badge {
  display: inline-flex; align-items: center;
  font-size: .82rem; font-weight: 700; white-space: nowrap;
  padding: 4px 11px; border-radius: 999px;
}
.tc-badge--available { color: #1f8a52; background: #dff3e6; }
.tc-badge--reserved  { color: #b07d13; background: #fbf1d8; }
.tc-badge--maint     { color: #5b6472; background: #eceef1; }

.tc-divider { height: 1px; background: #e0dbd3; margin: 11px 0 10px; }
.tc-foot { display: flex; align-items: center; flex-wrap: nowrap; gap: 6px 12px; min-height: 26px; }
.tc-staff {
  display: inline-flex; align-items: center; flex: 0 1 auto; min-width: 0;
  font-size: .7rem; font-weight: 700; color: #676d76;
  background: #f1efec; padding: 4px 11px; border-radius: 999px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.tc-staff i { color: #a7acb3; }
.tc-foot-item { display: inline-flex; align-items: center; gap: 6px; flex: 0 0 auto; font-size: .82rem; color: #676d76; white-space: nowrap; }
.tc-foot-item i { width: 13px; text-align: center; color: #a7acb3; font-size: .9em; }
.tc-foot-end { margin-left: auto; }

/*
  Customer-alert chips that float in the top-right of an occupied card.
  Positioned absolute so they don't reflow the existing two-column layout
  inside `.table-item`. `pointer-events: none` keeps card clicks intact —
  staff still taps the table itself to open the manage modal.
*/
/*
  Mobile (default): .table-item is a flex row, so let the alert chips flow
  inline right after the staff-badge instead of floating top-right where
  they would overlap with the time in .table-info-right.
*/
.alert-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-right: 4px;
  pointer-events: none;
}

/*
  Desktop: .table-item becomes column-flex and .staff-badge becomes
  absolute at top-right (see custom.scss), so float the alert chips at
  top-left to avoid overlap.
*/
@media (min-width: 768px) {
  .alert-stack {
    position: absolute;
    top: 10px;
    left: 10px;
    margin-right: 0;
    z-index: 2;
  }
}

.alert-chip {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  color: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  white-space: nowrap;
}

.alert-chip--call {
  background-color: #dc3545; /* danger */
  animation: alert-pulse 1.2s ease-in-out infinite;
}

.alert-chip--bill {
  background-color: #fd7e14; /* orange */
}

@keyframes alert-pulse {
  0%, 100% { box-shadow: 0 0 0 0   rgba(220, 53, 69, 0.55); }
  50%      { box-shadow: 0 0 0 8px rgba(220, 53, 69, 0);    }
}
</style>
